import { Button, Popover, PopoverContent, PopoverTrigger, TOUCH } from "@nymbl/nos2-rules";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { TaskSearch } from "@/components/time/TaskSearch";
import { taskLabel } from "@/lib/time/labels";
import { cn } from "@/lib/utils";
import type { AssignableTask } from "@/schemas/time";

interface MeetingTaskPickerProps {
  /** The meeting's name ("Weekly sync, Tue 13 10:00 AM"), so every picker is named uniquely. */
  name: string;
  /** The row's position on the page: each picker's search box gets its own id. */
  index: number;
  task: AssignableTask | null;
  onPick: (task: AssignableTask) => void;
  disabled?: boolean;
}

/** The task a meeting is logged to: a button showing it, opening the Log time task search. */
export function MeetingTaskPicker({ name, index, task, onPick, disabled }: MeetingTaskPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const shown = task ? taskLabel(task.taskName, task.projectName) : "Pick a task…";

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          aria-label={`Task for ${name}: ${shown}`}
          className={cn(
            TOUCH,
            "h-auto w-full min-w-0 justify-between gap-2 py-2 text-left font-normal whitespace-normal",
          )}
        >
          <span className={cn("line-clamp-2 break-words", !task && "text-muted-foreground")}>
            {shown}
          </span>
          <ChevronDown className="size-4 shrink-0 opacity-60" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(26rem,calc(100vw-2rem))]">
        <TaskSearch
          inputId={`meeting-q-${index}`}
          query={query}
          onQueryChange={setQuery}
          onPick={(picked) => {
            onPick(picked);
            setOpen(false);
            setQuery("");
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
